import { useCallback, useEffect, useState } from 'react';
import { UseTimeAgoProps } from './props';
import { useTranslation } from '../useTranslation';

/**
 * Custom hook to calculate and update the "time ago" string for a given date.
 * This hook provides a localized string representing how much time has passed since the given date.
 *
 * @param {UseTimeAgoProps} props - The properties for the hook.
 * @param {Date} props.date - The date to calculate the "time ago" from.
 * @returns {string} A string representing the time passed since the given date.
 *
 * @example
 * // Usage example:
 * const timeAgo = useTimeAgo({ date: new Date('2023-01-01') });
 */
export function useTimeAgo({ date }: UseTimeAgoProps): string {
  // Use the translation hook to get the translation function for the 'common' namespace
  const { t } = useTranslation('common');

  /**
   * Calculates the "time ago" string based on the current date and the provided date.
   * It returns a localized string indicating the time difference.
   *
   * @returns {string} The calculated "time ago" string.
   */
  const calculateTimeAgo = useCallback((): string => {
    if (!date) return '';

    const now = new Date().getTime();
    const diff = now - date.getTime();
    const minute = 60 * 1000;
    const hour = minute * 60;
    const day = hour * 24;
    const month = day * 30;
    const year = day * 365;

    switch (true) {
      case diff < minute:
        return t('time_ago_few_seconds');
      case diff < hour:
        const minutes = Math.round(diff / minute);
        if (minutes === 1) {
          return t('time_ago_a_minute');
        }
        return t('time_ago_minutes', { time: minutes });
      case diff < day:
        const hours = Math.round(diff / hour);
        if (hours === 1) {
          return t('time_ago_an_hour');
        }
        return t('time_ago_hours', { time: hours });
      case diff < month:
        const days = Math.round(diff / day);
        if (days === 1) {
          return t('time_ago_a_day');
        }
        return t('time_ago_days', { time: days });
      case diff < year:
        const months = Math.round(diff / month);
        if (months === 1) {
          return t('time_ago_a_month');
        }
        return t('time_ago_months', { time: months });
      case diff > year:
        const years = Math.round(diff / year);
        if (years === 1) {
          return t('time_ago_a_year');
        }
        return t('time_ago_years', { time: years });
      default:
        return '';
    }
  }, [date, t]);

  // State to store the "time ago" string, initialized with the calculated value
  const [timeAgo, setTimeAgo] = useState<string>(() => calculateTimeAgo());

  // Effect to update the "time ago" string every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeAgo(calculateTimeAgo());
    }, 60 * 1000);

    // Cleanup function to clear the interval when the component is unmounted
    return () => {
      clearInterval(timer);
    };
  }, [calculateTimeAgo]);

  // Return the "time ago" string
  return timeAgo;
}

export type { UseTimeAgoProps };
